import { jsx } from "react/jsx-runtime";
import {
  Circle2d,
  Polygon2d,
  SVGContainer,
  ShapeUtil,
  b64Vecs,
  debugFlags,
  getColorValue,
  highlightShapeMigrations,
  highlightShapeProps,
  last,
  lerp,
  rng,
  tlenvReactive,
  useColorMode,
  useValue
} from "@tldraw/editor";
import { getHighlightFreehandSettings, getPointsFromDrawSegments } from "../draw/getPath.mjs";
import { FONT_SIZES } from "../shared/default-shape-constants.mjs";
import { getStroke } from "../shared/freehand/getStroke.mjs";
import { getStrokePoints } from "../shared/freehand/getStrokePoints.mjs";
import { getSvgPathFromStrokePoints } from "../shared/freehand/svg.mjs";
import { getDisplayValues } from "../shared/getDisplayValues.mjs";
import { interpolateSegments } from "../shared/interpolate-props.mjs";
class HighlightShapeUtil extends ShapeUtil {
  static type = "highlight";
  static props = highlightShapeProps;
  static migrations = highlightShapeMigrations;
  options = {
    maxPointsPerShape: 600,
    getDefaultDisplayValues(_editor, shape, theme, colorMode) {
      const { color, size } = shape.props;
      const colors = theme.colors[colorMode];
      const useP3 = !debugFlags.forceSrgb.get() && tlenvReactive.get().supportsP3ColorSpace;
      const strokeColor = useP3 ? getColorValue(colors, color, "highlightP3") : getColorValue(colors, color, "highlightSrgb");
      return {
        strokeColor,
        strokeWidth: theme.fontSize * FONT_SIZES[size] * 1.12,
        underlayOpacity: 0.82,
        overlayOpacity: 0.35
      };
    },
    getCustomDisplayValues() {
      return {};
    }
  };
  hideResizeHandles(shape) {
    return getIsDot(shape);
  }
  hideRotateHandle(shape) {
    return getIsDot(shape);
  }
  hideSelectionBoundsFg(shape) {
    return getIsDot(shape);
  }
  getDefaultProps() {
    return {
      segments: [],
      color: "black",
      size: "m",
      isComplete: false,
      isPen: false,
      scale: 1,
      scaleX: 1,
      scaleY: 1
    };
  }
  getGeometry(shape) {
    const dv = getDisplayValues(this, shape);
    const strokeWidth = dv.strokeWidth * shape.props.scale;
    if (getIsDot(shape)) {
      return new Circle2d({
        x: -strokeWidth / 2,
        y: -strokeWidth / 2,
        radius: strokeWidth / 2,
        isFilled: true
      });
    }
    const allPointsFromSegments = getPointsFromDrawSegments(
      shape.props.segments,
      shape.props.scaleX,
      shape.props.scaleY
    );
    const showAsComplete = shape.props.isComplete || last(shape.props.segments)?.type === "straight";
    const opts = getHighlightFreehandSettings({ strokeWidth, showAsComplete });
    return new Polygon2d({
      points: getStroke(allPointsFromSegments, opts),
      isFilled: true
    });
  }
  component(shape) {
    const dv = getDisplayValues(this, shape);
    const sw = dv.strokeWidth * shape.props.scale;
    const forceSolid = useHighlightForceSolid(this.editor, sw);
    return /* @__PURE__ */ jsx(SVGContainer, { children: /* @__PURE__ */ jsx(
      HighlightRenderer,
      {
        shape,
        forceSolid,
        strokeWidth: sw,
        opacity: dv.overlayOpacity,
        strokeColor: dv.strokeColor
      }
    ) });
  }
  backgroundComponent(shape) {
    const colorMode = useColorMode();
    const dv = getDisplayValues(this, shape, colorMode);
    const sw = dv.strokeWidth * shape.props.scale;
    const forceSolid = useHighlightForceSolid(this.editor, sw);
    return /* @__PURE__ */ jsx(SVGContainer, { children: /* @__PURE__ */ jsx(
      HighlightRenderer,
      {
        shape,
        forceSolid,
        strokeWidth: sw,
        opacity: dv.underlayOpacity,
        strokeColor: dv.strokeColor
      }
    ) });
  }
  getIndicatorPath(shape) {
    const dv = getDisplayValues(this, shape);
    const strokeWidth = dv.strokeWidth * shape.props.scale;
    const zoomLevel = this.editor.getEfficientZoomLevel();
    const forceSolid = strokeWidth / zoomLevel < 1.5;
    const { strokePoints, sw } = getHighlightStrokePoints(shape, strokeWidth, forceSolid);
    const allPointsFromSegments = getPointsFromDrawSegments(
      shape.props.segments,
      shape.props.scaleX,
      shape.props.scaleY
    );
    let strokePath;
    if (strokePoints.length < 2) {
      strokePath = getIndicatorDot(allPointsFromSegments[0], sw);
    } else {
      strokePath = getSvgPathFromStrokePoints(strokePoints, false);
    }
    return new Path2D(strokePath);
  }
  toSvg(shape, ctx) {
    const dv = getDisplayValues(this, shape, ctx.colorMode);
    const strokeWidth = dv.strokeWidth * shape.props.scale;
    const forceSolid = strokeWidth < 1.5;
    const scaleFactor = 1 / shape.props.scale;
    return /* @__PURE__ */ jsx("g", { transform: `scale(${scaleFactor})`, children: /* @__PURE__ */ jsx(
      HighlightRenderer,
      {
        forceSolid,
        strokeWidth,
        shape,
        opacity: dv.overlayOpacity,
        strokeColor: dv.strokeColor
      }
    ) });
  }
  toBackgroundSvg(shape, ctx) {
    const dv = getDisplayValues(this, shape, ctx.colorMode);
    const strokeWidth = dv.strokeWidth * shape.props.scale;
    const forceSolid = strokeWidth < 1.5;
    const scaleFactor = 1 / shape.props.scale;
    return /* @__PURE__ */ jsx("g", { transform: `scale(${scaleFactor})`, children: /* @__PURE__ */ jsx(
      HighlightRenderer,
      {
        forceSolid,
        strokeWidth,
        shape,
        opacity: dv.underlayOpacity,
        strokeColor: dv.strokeColor
      }
    ) });
  }
  onResize(shape, info) {
    const newScaleX = info.scaleX * shape.props.scaleX;
    const newScaleY = info.scaleY * shape.props.scaleY;
    if (newScaleX === 0 || newScaleY === 0) return;
    return {
      props: {
        scaleX: newScaleX,
        scaleY: newScaleY
      }
    };
  }
  getInterpolatedProps(startShape, endShape, t) {
    return {
      ...(t > 0.5 ? endShape.props : startShape.props),
      ...endShape.props,
      segments: interpolateSegments(startShape.props.segments, endShape.props.segments, t),
      scale: lerp(startShape.props.scale, endShape.props.scale, t)
    };
  }
}
function getShapeDot(point) {
  const r = 0.1;
  return `M ${point.x} ${point.y} m -${r}, 0 a ${r},${r} 0 1,0 ${r * 2},0 a ${r},${r} 0 1,0 -${r * 2},0`;
}
function getIndicatorDot(point, sw) {
  const r = sw / 2;
  return `M ${point.x} ${point.y} m -${r}, 0 a ${r},${r} 0 1,0 ${r * 2},0 a ${r},${r} 0 1,0 -${r * 2},0`;
}
function getHighlightStrokePoints(shape, strokeWidth, forceSolid) {
  const allPointsFromSegments = getPointsFromDrawSegments(
    shape.props.segments,
    shape.props.scaleX,
    shape.props.scaleY
  );
  const showAsComplete = shape.props.isComplete || last(shape.props.segments)?.type === "straight";
  let sw = strokeWidth;
  if (!forceSolid && !shape.props.isPen && allPointsFromSegments.length === 1) {
    sw += rng(shape.id)() * (strokeWidth / 6);
  }
  const options = getHighlightFreehandSettings({
    strokeWidth: sw,
    showAsComplete
  });
  const strokePoints = getStrokePoints(allPointsFromSegments, options);
  return { strokePoints, sw };
}
function getIsDot(shape) {
  const segment = shape.props.segments[0];
  return shape.props.segments.length === 1 && b64Vecs.isSinglePoint(segment.path, segment.dim);
}
function HighlightRenderer({
  strokeWidth,
  forceSolid,
  shape,
  opacity,
  strokeColor
}) {
  const allPointsFromSegments = getPointsFromDrawSegments(
    shape.props.segments,
    shape.props.scaleX,
    shape.props.scaleY
  );
  let sw = strokeWidth;
  if (!forceSolid && !shape.props.isPen && allPointsFromSegments.length === 1) {
    sw += rng(shape.id)() * (sw / 6);
  }
  const options = getHighlightFreehandSettings({
    strokeWidth: sw,
    showAsComplete: shape.props.isComplete || last(shape.props.segments)?.type === "straight"
  });
  const strokePoints = getStrokePoints(allPointsFromSegments, options);
  const solidStrokePath = strokePoints.length > 1 ? getSvgPathFromStrokePoints(strokePoints, false) : getShapeDot(allPointsFromSegments[0]);
  return /* @__PURE__ */ jsx(
    "path",
    {
      d: solidStrokePath,
      strokeLinecap: "round",
      fill: "none",
      pointerEvents: "all",
      stroke: strokeColor,
      strokeWidth: sw,
      opacity
    }
  );
}
function useHighlightForceSolid(editor, scaledStrokeWidth) {
  return useValue(
    "forceSolid",
    () => {
      const zoomLevel = editor.getEfficientZoomLevel();
      return scaledStrokeWidth / zoomLevel < 1.5;
    },
    [editor, scaledStrokeWidth]
  );
}
export {
  HighlightShapeUtil
};
//# sourceMappingURL=HighlightShapeUtil.mjs.map
